import { Component, effect, inject, input, InputSignal, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SafeHtmlPipe } from '@core/pipes';
import { StorageService } from '@core/services';
import { Account } from '@legacy-models/portal/Account';
import { Contact } from '@legacy-models/portal/Contact';
import { HelpFeature } from '@legacy-models/strapi/Help';
import posthog from 'posthog-js';

interface VisibleFaqPair {
  cat: string;
  q: string;
  a: string;
}

@Component({
  selector: 'oke-faqs',
  imports: [ReactiveFormsModule, SafeHtmlPipe],
  templateUrl: './faqs.html',
  styleUrl: './faqs.scss'
})
export class Faqs implements OnInit {
  readonly $content: InputSignal<HelpFeature> = input.required<HelpFeature>({ alias: 'content' });
  readonly $isOnlyFeature: InputSignal<boolean> = input.required<boolean>({ alias: 'isOnlyFeature' });
  readonly $parentTopic: InputSignal<string> = input.required<string>({ alias: 'parentTopic' });
  allFaqCategories: string[] = [];
  visibleFaqPairs: VisibleFaqPair[] = [];
  manager = new FormGroup({
    category: new FormControl<string | null>(null)
  });
  private _allFaqPairs: VisibleFaqPair[] = [];
  private readonly _firstCategoryValue = 'All';
  private _user: Contact | null = null;
  private _acct: Account | null = null;
  private readonly _storageSvc: StorageService = inject(StorageService);

  constructor() {
    this._storageSvc.currentUser$.subscribe(currUser => {
      if (!currUser?.Contact) {
        return;
      }
      this._user = currUser.Contact;
    });
    this._storageSvc.currentAccount$.subscribe(currAcc => {
      this._acct = currAcc;
    });
    effect(() => {
      const updatedContent = this.$content();
      this.manager.reset();
      this.manager.get('category')!.setValue(this._firstCategoryValue, {
        emitEvent: false
      });
      if (updatedContent) {
        this._onContentUpdated();
      }
    });
  }

  ngOnInit(): void {
    this.manager.get('category')!.valueChanges.subscribe(this._onCategoryChange.bind(this));
    this._onContentUpdated();
  }

  // When a Help "section" is chosen, reset all of the FAQ data according to that updated content input
  private _onContentUpdated(): void {
    this.allFaqCategories = [];
    this.visibleFaqPairs = [];
    this._allFaqPairs = [];
    const faqs = this.$content().faqs;
    if (!faqs) {
      return;
    }

    this.allFaqCategories = Object.values(faqs).map(({ cat }) => cat);
    this.allFaqCategories.unshift(this._firstCategoryValue);
    this.visibleFaqPairs = Object.values(faqs).flatMap(({ cat, pairs }) => pairs.map(({ q, a }) => ({ cat, q, a })));
    this._allFaqPairs = this.visibleFaqPairs.map(faqPair => ({ ...faqPair }));
  }

  // When a different category--within a Help "section"--is chosen, reset (re-populate) the array of corresponding Qs & As
  private _onCategoryChange(formControlValue: string | null): void {
    if (formControlValue && formControlValue !== this.allFaqCategories[0]) {
      this.visibleFaqPairs = this._allFaqPairs.filter(({ cat }) => cat === formControlValue);
      posthog.capture('help:faq_category_click', {
        faq_category_name: formControlValue,
        parent_topic: this.$parentTopic(),
        number_of_faqs: this.visibleFaqPairs.length
      });
    } else {
      this.visibleFaqPairs = this._allFaqPairs.map(faqPair => ({ ...faqPair }));
      if (formControlValue === null) {
        return;
      }
      posthog.capture('help:faq_category_click', {
        faq_category_name: 'All',
        parent_topic: this.$parentTopic(),
        number_of_faqs: this.visibleFaqPairs.length
      });
    }
  }

  protected trackFaqItemClick(detailsToggleEvt: Event, question: string): void {
    posthog.capture('help:faq_item_click', {
      isRevealed: (detailsToggleEvt.target as HTMLDetailsElement).open,
      question
    });
  }

  parseStrapiTextLinks(strapiText: string): string {
    const convertedStrapiText = strapiText;
    /* NOPE
      const isUsr_accID=strapiText.includes("/acc_ID");
      if(isUsr_accID)
      {
        const rex = new RegExp('acc_ID','g');
        convertedStrapiText = strapiText.replace(rex,this.Acct.AccountId);
      }
      else
      {
        convertedStrapiText = strapiText;
      }
    */
    //if submit request page in Help
    const isHelpRequest = strapiText.includes('/request');
    if (isHelpRequest) {
      const rex = new RegExp('/request', 'g');
      return strapiText.replace(rex, '?pa=request');
    }
    return convertedStrapiText;
  }
}
